import { observable, action, computed, toJS, runInAction } from 'mobx';
import * as diffDays from 'date-fns/difference_in_calendar_days';
import * as addDays from 'date-fns/add_days';
import * as isPast from 'date-fns/is_past';
import { parse } from 'querystring';

import { RequestSource } from 'sletat-api-services/lib/module/getTours/getToursRequest';

import { GetToursRequestParams } from '../services/get-tours';
import { parseDateString } from '../utils/parsers';
import { HotelDetailsStore } from './hotel-details';
import { CURRENCY_ALIAS, DEFAULT_ADULTS_COUNT, DEFAULT_MAX_TOUR_NIGHTS, DEFAULT_MIN_TOUR_NIGHTS } from '../consts';
import {
    getInitialDepartureCityId,
    getInitialDepartureDateFrom,
    getInitialDepartureDateTo,
    getInitialKidsAges,
    getInitialMealsIds,
    getInitialNights,
    getInitialNumAdults,
    getInitialSelectedSources,
    getInitialTicketsIncluded,
} from '../utils/search-form';
import { formatDateDotted, formatDateSlashed } from '../utils/format';
import { getUserId, isTourist } from '../../utils/auth';
import { SearchParamsFromUrl } from '../models/search-form';
import { InitialStateStore } from './initial-state';
import { DepartureCity } from 'sletat-api-services/lib/sletat/models/initial-state/page-models/hotel';
import { convertMealsIds } from '../utils/convertMealsIds';
import { DEFAULT_DURATION_DEPARTURE_RANGE } from '../../consts/initial-state';
import { HotelPageStores } from '.';
import { getAvailableOperatorsForOnlineBilling } from '../services/get-operators-for-online-payment';
import { SLETAT_HOST_NAME_PUBLIC } from '../../consts/hosts';

export interface Range {
    startDate: Date;
    endDate: Date;
}

export interface NightsModel {
    min: number | null;
    max: number | null;
}

export interface DepartureDatesModel {
    date: string;
    dateIntervalChecked: boolean;
}

export interface DepartureDatesRangeModel {
    startDate: string;
    endDate: string;
}

interface TouristsModel {
    adults: number;
    kidsAges: Array<number>;
}

interface SearchParamsFromUrlModel {
    adults: string;
    dateFrom: string;
    dateTo: string;
    departCity: string;
    kids: string;
    nightsFrom: string;
    nightsTo: string;
    source_type: string;
    ticketsIncluded: string;
    mealsIds: string;
    operatorIds: string;
    roomType: string;
    onlineBilling: string;
}

export class SearchFormStore {
    @observable
    root!: HotelPageStores;

    @observable
    isSearchParamsModified = false;

    @observable
    isSearchFormBtnLoaderVisible = false;

    @observable
    departureCities: DepartureCity[];

    @observable
    selectedCityId: number | null;

    @observable
    selectedMealsTypes: string[] | null = null;

    @observable
    availableOperatorsIdsForOnlineBillingBySelectedCountry: number[] = [];

    @observable
    private _nights: NightsModel = {
        min: DEFAULT_MIN_TOUR_NIGHTS,
        max: DEFAULT_MAX_TOUR_NIGHTS,
    };
    @observable
    private _selectedSources: number[] = [];

    @observable
    private _departureDateRange: DepartureDatesRangeModel = {
        startDate: formatDateDotted(addDays(new Date(), 14)),
        endDate: formatDateDotted(addDays(new Date(), 21)),
    };

    @observable
    private _tourists: TouristsModel = {
        adults: DEFAULT_ADULTS_COUNT,
        kidsAges: [],
    };

    @observable
    private _isTicketsIncluded: boolean = true;

    constructor(
        private hotelDetails: HotelDetailsStore,
        private initialStateStore: InitialStateStore,
        // @ts-ignore next-line
        private _root: HotelPageStores,
    ) {
        this.root = _root;
        this.departureCities =
            this.initialStateStore.pageModel?.departureCities || [];
        this.selectedCityId = this.getInitialDepartureCityId();
    }

    @computed
    get selectedNights(): NightsModel {
        return this._nights;
    }

    @action
    changeNights(updatedNights: Partial<NightsModel>): void {
        this._nights = {
            ...this._nights,
            ...updatedNights,
        };

        if (!!updatedNights.min) {
            this.adjustMinNights(this._nights);
        }

        if (!!updatedNights.max) {
            this.adjustMaxNights(this._nights);
        }
    }

    @computed
    get selectedDepartureDatesRage(): DepartureDatesRangeModel {
        return this._departureDateRange;
    }

    @computed
    get selectedAdultsCount(): number {
        return this._tourists.adults;
    }

    @computed
    get selectedKidsCount(): number {
        return this._tourists.kidsAges.length;
    }

    @computed
    get selectedKidsAges(): Array<number> {
        return this._tourists.kidsAges;
    }

    @computed
    get isTicketsIncluded(): boolean {
        return this._isTicketsIncluded;
    }

    @computed
    get selectedSources(): number[] {
        return this._selectedSources;
    }

    @computed
    get getAviaSearchUrl(): string {
        return (
            `//${SLETAT_HOST_NAME_PUBLIC}/charters/#/flights?` +
            `depart_date=${this.selectedDepartureDatesRage.startDate}&` +
            `return_date=${this.selectedDepartureDatesRage.endDate}&` +
            `with_request=false&` +
            `adults=${this.selectedAdultsCount +
            this.selectedKidsAges.filter((c) => c > 12).length
            }&` +
            `children=${this.selectedKidsAges.filter((c) => c < 12 && c > 2).length
            }&` +
            `infants=${this.selectedKidsAges.filter((c) => c < 2).length}&` +
            `trip_class=0&locale=ru&one_way=false`
        );
    }

    get searchParamsFromHash(): SearchParamsFromUrl {
        const { href } = document.location;
        const separator = href.includes('?search') ? '?search' : '#search';
        const parameterStartIndex = href.indexOf(separator);
        const hashWithSearch = href.slice(parameterStartIndex);
        const separatorExtended = separator + (separator === '?search' ? '&' : '?');
        const searchParamsFromUrl = !!hashWithSearch
            ? parse(hashWithSearch.replace(separatorExtended, ''))
            : null as unknown as SearchParamsFromUrlModel;
        const departCity =
            !!searchParamsFromUrl &&
                searchParamsFromUrl.departCity !== null &&
                searchParamsFromUrl.departCity !== undefined
                ? parseInt(searchParamsFromUrl.departCity.toString(), 10)
                : null;
        const adults = !!searchParamsFromUrl &&
            searchParamsFromUrl.adults !== null &&
            searchParamsFromUrl.adults !== undefined
            ? parseInt(searchParamsFromUrl.adults.toString(), 10)
            : null;
        const dateFrom = !!searchParamsFromUrl &&
            searchParamsFromUrl.dateFrom !== null &&
            searchParamsFromUrl.dateFrom !== undefined
            ? searchParamsFromUrl.dateFrom.toString()
            : null;
        const dateTo = !!searchParamsFromUrl &&
            searchParamsFromUrl.dateTo !== null &&
            searchParamsFromUrl.dateTo !== undefined
            ? searchParamsFromUrl.dateTo.toString().replace('#', '')
            : null;
        const kids =
            !!searchParamsFromUrl && !!searchParamsFromUrl.kids?.length
                ? searchParamsFromUrl.kids
                    .toString()
                    .split(',')
                    .map((i) => parseInt(i, 10))
                : null;
        const nightsFrom = !!searchParamsFromUrl &&
            searchParamsFromUrl.nightsFrom !== null &&
            searchParamsFromUrl.nightsFrom !== undefined
            ? parseInt(searchParamsFromUrl.nightsFrom.toString(), 10)
            : null;
        const nightsTo = !!searchParamsFromUrl &&
            searchParamsFromUrl.nightsTo !== null &&
            searchParamsFromUrl.nightsTo !== undefined
            ? parseInt(searchParamsFromUrl.nightsTo.toString(), 10)
            : null;
        const ticketsIncluded =
            !!searchParamsFromUrl &&
                searchParamsFromUrl.ticketsIncluded !== null &&
                searchParamsFromUrl.ticketsIncluded !== undefined
                ? searchParamsFromUrl.ticketsIncluded.toString() === 'true'
                : null;
        const mealsIds =
            !!searchParamsFromUrl && searchParamsFromUrl.mealsIds
                ? searchParamsFromUrl.mealsIds.toString().split(',')
                : null;
        const operatorIds =
            !!searchParamsFromUrl && searchParamsFromUrl.operatorIds ? searchParamsFromUrl.operatorIds.toString().split(',') : null;
        const roomType =
            !!searchParamsFromUrl && searchParamsFromUrl.roomType
                ? searchParamsFromUrl.roomType.toString()
                : '';
        const onlineBilling =
            !!searchParamsFromUrl &&
                searchParamsFromUrl.onlineBilling !== null &&
                searchParamsFromUrl.onlineBilling !== undefined
                ? searchParamsFromUrl.onlineBilling.toString() === 'true'
                : null;
        return {
            departCity,
            nightsFrom,
            nightsTo,
            dateFrom,
            dateTo,
            adults,
            kids,
            ticketsIncluded,
            mealsIds,
            operatorIds,
            roomType,
            onlineBilling,
        };
    }

    updateURLRequestId(requestId: number): void {
        window.history.pushState('', '', `?requestId=${requestId}&source_type=${this.initialStateStore.sourceType}`);
    }

    get searchRequestParams(): GetToursRequestParams {
        return {
            cityFromId: this.selectedCityId!,
            countryToId: this.hotelDetails.countryId!,
            hotelsIds: [this.hotelDetails.hotelId!],
            minNightsCount: this.selectedNights.min!,
            maxNightsCount: this.selectedNights.max!,
            kidsCount: this.selectedKidsCount,
            kidsAges: toJS(this.selectedKidsAges),
            adultsCount: this.selectedAdultsCount,
            currency: CURRENCY_ALIAS,
            // Преобразуем дату в строку типа DD/MM/YYYY т.к поиск работает с таким шаблоном
            minDepartureDate: formatDateSlashed(
                parseDateString(this._departureDateRange.startDate)!
            ),
            maxDepartureDate: formatDateSlashed(
                parseDateString(this._departureDateRange.endDate)!
            ),
            isTicketsIncluded: this.isTicketsIncluded,
            hasTickets: true,
            hotelIsNotInStop: true,
            pageNumber: 1,
            pageSize: 9999,
            requestId: 0,
            updateResult: true,
            beachLines: [],
            calculateFullPrice: true,
            hotelsCategoriesIds: [],
            idsToFilter: [],
            includeDescriptions: true,
            includeOilTaxesAndVisa: true,
            mealsIds: [],
            operatorsIds: [],
            userId: getUserId(),
            requestSource: RequestSource.HotelCard,
            selectedSources: this._selectedSources,
            features: []
        };
    }

    @action
    changeDepartureCityId(id: number): void {
        this.selectedCityId = id;
    }

    @action
    setSelectedSources(sources: number[]): void {
        this._selectedSources = sources;
    }

    @action
    changeDepartureDateRange(range: DepartureDatesRangeModel): void {
        this._departureDateRange = {
            startDate: range.startDate,
            endDate: range.endDate,
        };
    }

    @action
    changeTourists(touristsInfo: Partial<TouristsModel>): void {
        this._tourists = {
            ...this._tourists,
            ...touristsInfo,
        };
    }

    @action
    setIsTicketsIncluded(isTicketsIncluded: boolean): void {
        this._isTicketsIncluded = isTicketsIncluded;
    }

    @action
    setIsSearchParamsModified(modified: boolean): void {
        this.isSearchParamsModified = modified;
    }

    @action
    setSsrInitialStateStore(store: InitialStateStore): void {
        this.initialStateStore = store;
        this.departureCities =
            this.initialStateStore.pageModel!.departureCities;
        this.selectedCityId = this.getInitialDepartureCityId();
    }

    @action
    initSearchForm(): void {
        this.selectedCityId =
            this.searchParamsFromHash.departCity ||
            getInitialDepartureCityId() ||
            this.selectedCityId;

        const touristsModel: TouristsModel | null = !!this.searchParamsFromHash
            .adults
            ? {
                adults: this.searchParamsFromHash.adults,
                kidsAges: this.searchParamsFromHash.kids || [],
            }
            : null;

        !!touristsModel
            ? this.changeTourists(touristsModel)
            : this.changeTourists({
                adults: getInitialNumAdults() || this.selectedAdultsCount,
                kidsAges: getInitialKidsAges() || this.selectedKidsAges,
            });

        const parseDateFrom = !!this.searchParamsFromHash.dateFrom
            ? parseDateString(this.searchParamsFromHash.dateFrom)
            : null;
        const parseDateTo = !!this.searchParamsFromHash.dateTo
            ? parseDateString(this.searchParamsFromHash.dateTo)
            : null;

        const days =
            !!parseDateFrom && !!parseDateTo
                ? diffDays(
                    parseDateString(this.searchParamsFromHash.dateTo!)!,
                    parseDateString(this.searchParamsFromHash.dateFrom!)!
                )
                : null;

        let departDateModel =
            days !== null ?
                {
                    startDate: parseDateString(
                        this.searchParamsFromHash.dateFrom!
                    ),
                    endDate: parseDateString(
                        this.searchParamsFromHash.dateTo!
                    ),
                }
                : {
                    startDate: parseDateString(
                        getInitialDepartureDateFrom()!
                    ),
                    endDate: getEndDateHandler(
                        parseDateString(
                            getInitialDepartureDateFrom()!
                        ) as Date,
                        parseDateString(getInitialDepartureDateTo()!) as Date,
                        DEFAULT_DURATION_DEPARTURE_RANGE
                    ),
                };

        // Для проверки добавляем один день, так как isPast считает сегодняшний день прошедшим
        if (!departDateModel.startDate ||
            !departDateModel.endDate ||
            isPast(addDays(departDateModel.startDate, 1)) ||
            isPast(addDays(departDateModel.endDate, 1))
        ) {
            const startDate = addDays(new Date(), 7);

            departDateModel = {
                startDate,
                endDate: addDays(startDate, DEFAULT_DURATION_DEPARTURE_RANGE),
            };
        }


        function getEndDateHandler(
            startDate: Date,
            endDate: Date,
            duration: number
        ): Date {
            const isNotTourist = !isTourist();

            return isNotTourist && endDate > addDays(startDate, duration)
                ? addDays(startDate, duration)
                : endDate;
        }

        if (!!departDateModel.startDate && !!departDateModel.endDate) {
            this.changeDepartureDateRange({
                startDate: formatDateDotted(departDateModel.startDate),
                endDate: formatDateDotted(departDateModel.endDate),
            });
        } else {
            this.changeDepartureDateRange({
                startDate: this.selectedDepartureDatesRage.startDate,
                endDate: this.selectedDepartureDatesRage.endDate,
            });
        }

        const nightsModel =
            !!this.searchParamsFromHash.nightsFrom &&
                !!this.searchParamsFromHash.nightsTo
                ? {
                    min: this.searchParamsFromHash.nightsFrom,
                    max: this.searchParamsFromHash.nightsTo,
                }
                : getInitialNights();

        this.changeNights({
            ...nightsModel,
        });

        const isTicketsIncluded =
            this.searchParamsFromHash.ticketsIncluded !== null
                ? this.searchParamsFromHash.ticketsIncluded
                : getInitialTicketsIncluded();
        this.setIsTicketsIncluded(isTicketsIncluded);
        this.setSelectedSources(getInitialSelectedSources());

        this.selectedMealsTypes = this.searchParamsFromHash.mealsIds?.map((mealId) =>
            convertMealsIds(mealId)) ?? getInitialMealsIds()?.map(it => convertMealsIds(it)) ?? null;

        if (this.initialStateStore.pageModel?.countryDetails.id) {
            this.updateAvailableOperatorsForOnlineBillingByHotelCountry(this.initialStateStore.pageModel?.countryDetails.id);
        }
    }

    changeTicketsIncludedHandler(isTicketsIncluded: boolean): void {
        this.setIsTicketsIncluded(isTicketsIncluded);
        this.setIsSearchParamsModified(true);
    }

    changeDepartureDatesHandler(startDate: Date, endDate: Date): void {
        this.changeDepartureDateRange({ startDate: formatDateDotted(startDate), endDate: formatDateDotted(endDate) });
        this.setIsSearchParamsModified(true);
    }

    showSearchFormBtnLoader(): void {
        runInAction(() => (this.isSearchFormBtnLoaderVisible = true));
        setTimeout(() => {
            runInAction(() => (this.isSearchFormBtnLoaderVisible = false));
        }, 3000);
    }

    // сначало по геолокации, иначе просто первый в списке
    private getInitialDepartureCityId(): number | null {
        const geoCity = this.initialStateStore.geoCity;
        const cities = this.departureCities;

        const foundGeoCity = geoCity
            ? cities.filter((c) => c.id === geoCity.cityFromId)[0] || null
            : null;

        return foundGeoCity
            ? foundGeoCity.id
            : cities.length
                ? cities[0].id
                : null;
    }

    @action
    private adjustMinNights(nights: NightsModel): void {
        const { min, max } = nights;

        if (min! >= max!) {
            this._nights.max = nights.min;
        }
    }

    @action
    private adjustMaxNights(nights: NightsModel): void {
        const { min, max } = nights;

        if (max! <= min!) {
            this._nights.min = nights.max;
        }
    }

    /**
     * Запрашивает список всех доступных для онлайн-оплаты операторов в выбранную страну
     * (не только тех, что доступны для поиска)
     */
    private async updateAvailableOperatorsForOnlineBillingByHotelCountry(countryToId: number): Promise<void> {
        try {
            const operators = await getAvailableOperatorsForOnlineBilling(
                SLETAT_HOST_NAME_PUBLIC,
                countryToId
            );
            this.availableOperatorsIdsForOnlineBillingBySelectedCountry = operators;
        } catch (e) {
            console.error('Getting available operators for online payment has failed', e);
        }
    }
}
